import { useEffect, useState } from "react";
import api, { API } from "@/lib/api";
import { fmtIST } from "@/lib/utils";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { FloppyDisk, Sparkle, UserPlus, PaperPlaneTilt, Trash, ClockCounterClockwise, PencilSimple, TreeStructure, Table as TableIcon } from "@phosphor-icons/react";
import { toast } from "sonner";

const NONE = "__none__";
const roleToTsRole = (role) => ({ agent: "user", manager: "manager", admin: "admin", timesheet_admin: "admin" }[role] || "user");

const ROLE_STYLES = {
  admin: "border-primary/50 text-primary",
  manager: "border-violet-400/40 text-violet-300",
  agent: "border-sky-400/40 text-sky-700 dark:text-sky-300",
  end_user: "border-border text-muted-foreground",
  soc_analyst: "border-red-400/40 text-red-600 dark:text-red-400",
  soc_lead: "border-red-500/50 text-red-700 dark:text-red-300",
  soc_auditor: "border-amber-400/40 text-amber-600 dark:text-amber-400",
  soc_viewer: "border-sky-400/40 text-sky-600 dark:text-sky-300",
  timesheet_admin: "border-teal-400/40 text-teal-600 dark:text-teal-300",
};
const ROLES = ["admin", "manager", "agent", "end_user", "soc_analyst", "soc_lead", "soc_auditor", "soc_viewer", "timesheet_admin"];
const ROLE_GROUPS = [
  { key: "all", label: "All", roles: null },
  { key: "admins", label: "Admins", roles: ["admin", "timesheet_admin"] },
  { key: "agents", label: "Agents", roles: ["agent", "manager"] },
  { key: "soc", label: "SOC", roles: ["soc_analyst", "soc_lead", "soc_auditor", "soc_viewer"] },
  { key: "end_users", label: "End Users", roles: ["end_user"] },
];

export default function Users() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [jobRoles, setJobRoles] = useState([]);
  const [managers, setManagers] = useState([]);
  const [invite, setInvite] = useState({ email: "", name: "", role: "agent" });
  const [inviting, setInviting] = useState(false);
  const [tsOnly, setTsOnly] = useState(false);
  const [hideInactive, setHideInactive] = useState(false);
  const [roleFilter, setRoleFilter] = useState("all");
  const [view, setView] = useState("table");
  const [activity, setActivity] = useState({});
  const isSuper = user?.role === "admin";
  const isAdmin = isSuper || user?.role === "timesheet_admin";  // "can manage" (admin or timesheet_admin)
  const ASSIGNABLE_ROLES = isSuper ? ROLES : ROLES.filter(r => !["admin", "timesheet_admin"].includes(r));

  const load = () => {
    const applyList = (list) => setUsers(list);
    if (isAdmin) {
      Promise.all([api.get("/timesheet/users"), api.get("/timesheet/job-roles"), api.get("/timesheet/managers")])
        .then(([u, r, m]) => { applyList(u.data.users); setJobRoles(r.data); setManagers(m.data); })
        .catch(() => api.get("/users").then(r => applyList(r.data)));
      api.get("/users/activity").then(r => setActivity(r.data || {})).catch(() => {});
    } else {
      api.get("/users").then(r => applyList(r.data));
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  // id -> display name, for showing the Manager column. Includes the managers roster too.
  const nameById = {};
  users.forEach(u => { nameById[u.id] = u.name; });
  managers.forEach(m => { if (!nameById[m.id]) nameById[m.id] = m.name; });

  // ---- Edit modal ----
  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const canEditRole = (u) => isSuper && u.id !== user?.id && !u.is_protected;
  const openEdit = (u) => {
    setEditUser(u);
    setEditForm({
      role: u.role,
      name: u.name || "",
      timesheet_access: !!u.timesheet_access,
      ts_job_role_id: u.ts_job_role_id || null,
      ts_manager_id: u.ts_manager_id || null,
      skills: (u.skills || []).join(", "),
      soc_tier: u.soc_tier || 0,
    });
  };
  const saveEdit = async () => {
    const u = editUser, f = editForm;
    setSavingEdit(true);
    try {
      if (isAdmin && f.name.trim() && f.name.trim() !== (u.name || "")) {
        await api.patch(`/users/${u.id}/name`, { name: f.name.trim() });
      }
      if (canEditRole(u) && f.role !== u.role) {
        await api.patch(`/users/${u.id}/role`, { role: f.role });
      }
      if (isAdmin && f.skills !== (u.skills || []).join(", ")) {
        const skills = f.skills.split(",").map(s => s.trim()).filter(Boolean);
        await api.patch(`/users/${u.id}/skills`, { skills });
      }
      if (isAdmin) {
        await api.put(`/timesheet/users/${u.id}`, {
          timesheet_access: !!f.timesheet_access,
          timesheet_role: roleToTsRole(f.role),
          ts_job_role_id: f.ts_job_role_id || null,
          ts_manager_id: f.ts_manager_id || null,
        });
      }
      if (isSuper && u.role === "soc_analyst" && (f.soc_tier || 0) !== (u.soc_tier || 0)) {
        await api.patch(`/soc/analysts/${u.id}/tier`, { tier: f.soc_tier ? f.soc_tier : null });
      }
      toast.success(`Saved ${u.name}`);
      setEditUser(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally { setSavingEdit(false); }
  };

  const sendInvite = async () => {
    if (!invite.email.trim()) { toast.error("Email is required"); return; }
    setInviting(true);
    try {
      const { data } = await api.post("/users/invite", invite);
      const es = data.email?.status;
      if (es === "sent") toast.success(`Invited ${data.user.email} — email sent`);
      else toast.success(`User ${data.user.email} created — email ${es === "simulated" ? "simulated (configure Microsoft 365 delivery to send)" : es}`);
      setInvite({ email: "", name: "", role: "agent" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to invite");
    } finally { setInviting(false); }
  };

  const [removing, setRemoving] = useState(null);
  const [auditOpen, setAuditOpen] = useState(false);
  const [audit, setAudit] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditUser, setAuditUser] = useState(NONE);
  const [auditAction, setAuditAction] = useState(NONE);
  const auditParams = () => {
    const p = new URLSearchParams();
    if (auditUser !== NONE) p.set("target_id", auditUser);
    if (auditAction !== NONE) p.set("action", auditAction);
    return p;
  };
  const loadAudit = async () => {
    setAuditLoading(true);
    try { const { data } = await api.get(`/users/audit?${auditParams().toString()}`); setAudit(data); }
    catch { toast.error("Could not load history"); }
    finally { setAuditLoading(false); }
  };
  const openAudit = () => { setAuditOpen(true); loadAudit(); };
  useEffect(() => { if (auditOpen) loadAudit(); /* eslint-disable-next-line */ }, [auditUser, auditAction]);
  const exportAudit = async () => {
    try {
      const token = localStorage.getItem("helix_token");
      const res = await fetch(`${API}/users/audit/csv?${auditParams().toString()}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error();
      const url = URL.createObjectURL(await res.blob());
      const a = document.createElement("a"); a.href = url; a.download = "user-audit.csv";
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };
  const [suspendingId, setSuspendingId] = useState(null);
  const suspendUser = async (u, suspended) => {
    if (suspended && !window.confirm(`Suspend ${u.name}? They will be signed out and blocked from signing in until reactivated. Their history is kept.`)) return;
    setSuspendingId(u.id);
    try {
      await api.patch(`/users/${u.id}/suspend`, { suspended });
      toast.success(suspended ? `Suspended ${u.name}` : `Reactivated ${u.name}`);
      setEditUser(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update access");
    } finally { setSuspendingId(null); }
  };

  const removeUser = async (u) => {
    if (!window.confirm(`Remove ${u.name} (${u.email})? Their open tickets will be unassigned. This cannot be undone.`)) return;
    setRemoving(u.id);
    try {
      await api.delete(`/users/${u.id}`);
      toast.success(`Removed ${u.email}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to remove");
    } finally { setRemoving(null); }
  };

  const signedIn = (u) => !!activity[u.id];
  const roleGroup = ROLE_GROUPS.find(g => g.key === roleFilter) || ROLE_GROUPS[0];
  const roleCount = (g) => g.roles ? users.filter(u => g.roles.includes(u.role)).length : users.length;
  const shown = (isAdmin && tsOnly ? users.filter(u => u.timesheet_access) : users)
    .filter(u => !(hideInactive && isAdmin && !signedIn(u)))
    .filter(u => !roleGroup.roles || roleGroup.roles.includes(u.role));
  const neverCount = users.filter(u => !signedIn(u)).length;

  // Build a manager -> reports tree from ts_manager_id (the single Manager field).
  const idSet = new Set(users.map(u => u.id));
  const childrenBy = {};
  users.forEach(u => {
    const key = (u.ts_manager_id && idSet.has(u.ts_manager_id)) ? u.ts_manager_id : "__root__";
    (childrenBy[key] = childrenBy[key] || []).push(u);
  });
  const sortByName = (a, b) => a.name.localeCompare(b.name);
  const roots = (childrenBy["__root__"] || []).slice().sort(sortByName);

  const renderNode = (u, depth, visited) => {
    if (visited.has(u.id)) return null;
    const nv = new Set(visited); nv.add(u.id);
    const kids = (childrenBy[u.id] || []).slice().sort(sortByName);
    return (
      <div key={u.id} className={depth > 0 ? "ml-4 pl-4 border-l border-border/70" : ""} data-testid={`org-node-${u.email}`}>
        <div className="flex items-center gap-2 py-1.5 group">
          <button
            onClick={() => isAdmin && openEdit(u)}
            className={`text-sm font-medium text-left ${isAdmin ? "hover:text-primary" : "cursor-default"}`}
            title={isAdmin ? "Edit" : ""}
          >{u.name}</button>
          <span className={`text-[9px] font-mono uppercase tracking-wider px-1 py-0.5 border ${ROLE_STYLES[u.role] || ""}`}>{u.role}</span>
          {kids.length > 0 && <span className="text-[10px] font-mono text-muted-foreground">{kids.length} report{kids.length > 1 ? "s" : ""}</span>}
          {u.suspended && <span className="text-[9px] font-mono uppercase px-1 py-0.5 border border-red-500/50 text-red-700 dark:text-red-400 rounded-sm">suspended</span>}
          {isAdmin && !signedIn(u) && <span className="text-[9px] font-mono uppercase px-1 py-0.5 border border-amber-500/50 text-amber-700 dark:text-amber-400 rounded-sm">pending</span>}
        </div>
        {kids.length > 0 && <div className="mb-0.5">{kids.map(k => renderNode(k, depth + 1, nv))}</div>}
      </div>
    );
  };

  return (
    <div className="p-6" data-testid="users-view">
      <div className="mb-6">
        <div className="text-[11px] font-mono uppercase tracking-widest text-primary mb-1">Team</div>
        <div className="flex items-start justify-between gap-4">
          <h1 className="font-display text-3xl sm:text-4xl font-black">Users &amp; Skills</h1>
          {isAdmin && (
            <Button variant="outline" className="rounded-sm font-mono uppercase text-xs shrink-0" onClick={openAudit} data-testid="role-audit-open">
              <ClockCounterClockwise size={15} className="mr-1.5" /> Change history
            </Button>
          )}
        </div>
        <p className="text-sm text-muted-foreground mt-2 max-w-3xl">
          Manage team members in one place. Each row shows the essentials — click <span className="font-mono text-primary">Edit</span> to change role, <span className="font-mono text-primary">manager</span>, timesheet access, job role, SOC tier and skill tags.
        </p>
      </div>

      {!isAdmin && (
        <Card className="p-3 border-amber-400/40 rounded-sm mb-4 bg-amber-400/5">
          <div className="text-xs font-mono text-amber-700 dark:text-amber-300">Read-only — user management is restricted to admins.</div>
        </Card>
      )}

      {isAdmin && (
        <Card className="p-5 border-border rounded-sm mb-4" data-testid="invite-card">
          <div className="flex items-center gap-2 mb-4">
            <UserPlus size={16} className="text-primary" />
            <span className="text-[11px] font-mono uppercase tracking-widest">Invite a team member</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_180px_auto] gap-3 items-end">
            <div>
              <label className="text-[10px] font-mono uppercase text-muted-foreground">Work email</label>
              <Input data-testid="invite-email" className="mt-1 rounded-sm font-mono text-sm" placeholder="name@company.com" value={invite.email} onChange={(e) => setInvite({ ...invite, email: e.target.value })} />
            </div>
            <div>
              <label className="text-[10px] font-mono uppercase text-muted-foreground">Name (optional)</label>
              <Input data-testid="invite-name" className="mt-1 rounded-sm text-sm" placeholder="Jane Doe" value={invite.name} onChange={(e) => setInvite({ ...invite, name: e.target.value })} />
            </div>
            <div>
              <label className="text-[10px] font-mono uppercase text-muted-foreground">Role</label>
              <Select value={invite.role} onValueChange={(v) => setInvite({ ...invite, role: v })}>
                <SelectTrigger data-testid="invite-role" className="mt-1 rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent>{ASSIGNABLE_ROLES.map(r => <SelectItem key={r} value={r}>{r.replace("_", " ")}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Button data-testid="invite-submit" onClick={sendInvite} disabled={inviting} className="rounded-sm font-mono uppercase text-[11px] tracking-widest h-10">
              <PaperPlaneTilt size={14} className="mr-1.5" /> {inviting ? "Sending…" : "Send invite"}
            </Button>
          </div>
          <p className="text-[11px] text-muted-foreground mt-2">A "Sign in to TRAK" email is sent via Microsoft 365 (Office 365 / Graph) if configured; otherwise the account is created and the email is simulated.</p>
        </Card>
      )}

      <Card className="border-border rounded-sm overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-border bg-muted/20" data-testid="users-filter-bar">
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
            {isAdmin && (
              <label className="flex items-center gap-2 cursor-pointer">
                <Switch checked={tsOnly} onCheckedChange={setTsOnly} data-testid="ts-only-toggle" disabled={view === "org"} />
                <span className="text-xs font-mono uppercase tracking-wide text-muted-foreground">Timesheet users only</span>
                {tsOnly && view === "table" && <span className="text-[11px] font-mono text-primary">· {users.filter(u => u.timesheet_access).length} of {users.length}</span>}
              </label>
            )}
            {isAdmin && (
              <label className="flex items-center gap-2 cursor-pointer">
                <Switch checked={hideInactive} onCheckedChange={setHideInactive} data-testid="hide-inactive-toggle" disabled={view === "org"} />
                <span className="text-xs font-mono uppercase tracking-wide text-muted-foreground">Hide never signed-in</span>
                {neverCount > 0 && <span className="text-[11px] font-mono text-amber-700 dark:text-amber-400" data-testid="never-count-hint">· {neverCount} pending</span>}
              </label>
            )}
            {view === "table" && (
              <div className="flex items-center gap-1 bg-background border border-border rounded-sm p-0.5" data-testid="users-role-filter">
                {ROLE_GROUPS.map(g => (
                  <button
                    key={g.key}
                    data-testid={`role-filter-${g.key}`}
                    onClick={() => setRoleFilter(g.key)}
                    className={`px-2.5 py-1 rounded-sm text-[10px] font-mono uppercase tracking-wider flex items-center gap-1.5 ${roleFilter === g.key ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
                  >
                    {g.label}
                    <span className={`text-[9px] ${roleFilter === g.key ? "text-primary-foreground/80" : "text-muted-foreground/70"}`}>{roleCount(g)}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="flex items-center gap-1 bg-background border border-border rounded-sm p-0.5" data-testid="users-view-toggle">
            <button data-testid="users-view-table" onClick={() => setView("table")}
              className={`px-2.5 py-1 rounded-sm text-[10px] font-mono uppercase tracking-wider flex items-center gap-1.5 ${view === "table" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
              <TableIcon size={13} /> Table
            </button>
            <button data-testid="users-view-org" onClick={() => setView("org")}
              className={`px-2.5 py-1 rounded-sm text-[10px] font-mono uppercase tracking-wider flex items-center gap-1.5 ${view === "org" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
              <TreeStructure size={13} /> Org chart
            </button>
          </div>
        </div>

        {view === "org" ? (
          <div className="p-5" data-testid="users-org-chart">
            <p className="text-[11px] text-muted-foreground mb-3">Reporting structure by Manager. {isAdmin ? "Click a name to edit." : ""}</p>
            {roots.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground text-sm">No users to chart.</div>
            ) : (
              <div className="space-y-1">{roots.map(u => renderNode(u, 0, new Set()))}</div>
            )}
          </div>
        ) : (
        <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[680px]">
          <thead>
            <tr className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono border-b border-border">
              <th className="text-left px-4 py-3">Name</th>
              <th className="text-left px-4 py-3">Role</th>
              <th className="text-center px-4 py-3">TS Access</th>
              <th className="text-left px-4 py-3">Manager</th>
              <th className="text-right px-4 py-3">{isAdmin ? "Actions" : ""}</th>
            </tr>
          </thead>
          <tbody>
            {shown.map(u => (
              <tr key={u.id} className="border-b border-border/60" data-testid={`user-row-${u.email}`}>
                <td className="px-4 py-3 whitespace-nowrap">
                  <div className="font-medium text-foreground leading-tight flex items-center gap-2">
                    {u.name}
                    {u.suspended && <span className="text-[9px] font-mono uppercase px-1 py-0.5 border border-red-500/50 text-red-700 dark:text-red-400 rounded-sm" data-testid={`user-suspended-${u.email}`}>suspended</span>}
                    {isAdmin && !signedIn(u) && <span className="text-[9px] font-mono uppercase px-1 py-0.5 border border-amber-500/50 text-amber-700 dark:text-amber-400 rounded-sm" data-testid={`user-pending-${u.email}`}>pending</span>}
                  </div>
                  <div className="font-mono text-[11px] text-muted-foreground leading-tight" title={u.email}>{u.email}</div>
                </td>
                <td className="px-4 py-3">
                  <span className={`text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 border ${ROLE_STYLES[u.role] || ""}`} data-testid={`user-role-badge-${u.email}`} title={u.is_protected ? "Protected admin account" : ""}>
                    {u.role}{u.is_protected ? " 🔒" : ""}
                  </span>
                  {u.role === "soc_analyst" && u.soc_tier ? <span className="ml-1.5 text-[10px] font-mono text-muted-foreground">T{u.soc_tier}</span> : null}
                </td>
                <td className="px-4 py-3 text-center">
                  <span className={`text-[10px] font-mono uppercase px-1.5 py-0.5 border rounded-sm ${u.timesheet_access ? "border-emerald-500/50 text-emerald-700 dark:text-emerald-400" : "border-border text-muted-foreground"}`} data-testid={`user-ts-access-${u.email}`}>
                    {u.timesheet_access ? "Yes" : "No"}
                  </span>
                </td>
                <td className="px-4 py-3 text-muted-foreground" data-testid={`user-manager-${u.email}`}>
                  {u.ts_manager_id ? (nameById[u.ts_manager_id] || "—") : "—"}
                </td>
                <td className="px-4 py-3 text-right">
                  {isAdmin && (
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        data-testid={`user-edit-${u.email}`}
                        onClick={() => openEdit(u)}
                        size="sm"
                        variant="outline"
                        className="rounded-sm font-mono uppercase text-[10px] h-8"
                      >
                        <PencilSimple size={12} className="mr-1.5" /> Edit
                      </Button>
                      {isSuper && u.id !== user?.id && !u.is_protected && (
                        <Button
                          data-testid={`user-remove-${u.email}`}
                          onClick={() => removeUser(u)}
                          disabled={removing === u.id}
                          size="sm"
                          variant="outline"
                          className="rounded-sm font-mono uppercase text-[10px] h-8 border-red-500/40 text-red-700 dark:text-red-400 hover:bg-red-500/10 hover:text-red-700 dark:hover:text-red-300"
                          title="Remove user"
                        >
                          <Trash size={12} />
                        </Button>
                      )}
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {shown.length === 0 && (
              <tr><td colSpan={5} className="text-center py-8 text-muted-foreground text-sm">{isAdmin && tsOnly ? "No timesheet users yet." : "No agents or admins found."}</td></tr>
            )}
          </tbody>
        </table>
        </div>
        )}
      </Card>

      {/* Edit user modal */}
      <Dialog open={!!editUser} onOpenChange={(o) => { if (!o) setEditUser(null); }}>
        <DialogContent className="rounded-sm max-w-lg" data-testid="user-edit-dialog">
          {editUser && editForm && (
            <>
              <DialogHeader>
                <DialogTitle className="font-mono uppercase text-sm flex items-center gap-2"><PencilSimple size={16} /> Edit {editUser.name}</DialogTitle>
              </DialogHeader>
              <div className="text-[11px] font-mono text-muted-foreground -mt-1">{editUser.email}</div>
              <div className="space-y-4 py-1">
                <div>
                  <Label className="text-[10px] font-mono uppercase text-muted-foreground">Name</Label>
                  <Input
                    data-testid="edit-name-input"
                    className="mt-1 rounded-sm text-sm"
                    placeholder="Full name"
                    value={editForm.name}
                    onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  />
                </div>
                <div>
                  <Label className="text-[10px] font-mono uppercase text-muted-foreground">Role</Label>
                  {canEditRole(editUser) ? (
                    <Select value={editForm.role} onValueChange={(v) => setEditForm({ ...editForm, role: v })}>
                      <SelectTrigger data-testid="edit-role-select" className="mt-1 rounded-sm font-mono text-xs uppercase"><SelectValue /></SelectTrigger>
                      <SelectContent>{ASSIGNABLE_ROLES.map(r => <SelectItem key={r} value={r} className="text-xs uppercase font-mono">{r.replace("_", " ")}</SelectItem>)}</SelectContent>
                    </Select>
                  ) : (
                    <div className="mt-1"><span className={`text-[10px] font-mono uppercase px-1.5 py-0.5 border ${ROLE_STYLES[editUser.role] || ""}`}>{editUser.role}</span>
                      <span className="ml-2 text-[10px] text-muted-foreground">{editUser.is_protected ? "Protected account" : editUser.id === user?.id ? "You can't change your own role" : "Role locked"}</span>
                    </div>
                  )}
                </div>

                {isSuper && editForm.role === "soc_analyst" && (
                  <div>
                    <Label className="text-[10px] font-mono uppercase text-muted-foreground">SOC tier</Label>
                    <Select value={String(editForm.soc_tier || 0)} onValueChange={(v) => setEditForm({ ...editForm, soc_tier: parseInt(v, 10) })}>
                      <SelectTrigger data-testid="edit-soc-tier" className="mt-1 rounded-sm text-xs font-mono uppercase"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0" className="text-xs font-mono">No tier</SelectItem>
                        <SelectItem value="1" className="text-xs font-mono">Tier 1</SelectItem>
                        <SelectItem value="2" className="text-xs font-mono">Tier 2</SelectItem>
                        <SelectItem value="3" className="text-xs font-mono">Tier 3</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {isAdmin && (
                  <div className="flex items-center justify-between gap-3 rounded-sm border border-border/60 px-3 py-2.5">
                    <div>
                      <div className="text-sm font-semibold">Timesheet access</div>
                      <div className="text-[11px] text-muted-foreground">Allow this user to log &amp; submit timesheets.</div>
                    </div>
                    <Switch data-testid="edit-ts-access" checked={!!editForm.timesheet_access} onCheckedChange={(v) => setEditForm({ ...editForm, timesheet_access: v })} />
                  </div>
                )}

                {isAdmin && (
                  <div>
                    <Label className="text-[10px] font-mono uppercase text-muted-foreground">Manager</Label>
                    <Select value={editForm.ts_manager_id || NONE} onValueChange={(v) => setEditForm({ ...editForm, ts_manager_id: v === NONE ? null : v })}>
                      <SelectTrigger data-testid="edit-manager-select" className="mt-1 rounded-sm text-xs"><SelectValue placeholder="Manager" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value={NONE}>— No manager —</SelectItem>
                        {managers.filter((m) => m.id !== editUser.id).map((m) => (<SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>))}
                      </SelectContent>
                    </Select>
                    <p className="text-[11px] text-muted-foreground mt-1">Used for team mapping across ITSM, Timesheet and SOC reports.</p>
                  </div>
                )}

                {isAdmin && (
                  <div>
                    <Label className="text-[10px] font-mono uppercase text-muted-foreground">Job role</Label>
                    <Select value={editForm.ts_job_role_id || NONE} onValueChange={(v) => setEditForm({ ...editForm, ts_job_role_id: v === NONE ? null : v })}>
                      <SelectTrigger data-testid="edit-jobrole-select" className="mt-1 rounded-sm text-xs"><SelectValue placeholder="Job role" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value={NONE}>— No job role —</SelectItem>
                        {jobRoles.map((r) => (<SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {isAdmin && (
                  <div>
                    <Label className="text-[10px] font-mono uppercase text-muted-foreground flex items-center gap-1.5"><Sparkle size={12} className="text-primary" /> Skills (comma-separated auto-assignment tags)</Label>
                    <Input
                      data-testid="edit-skills-input"
                      className="mt-1 rounded-sm font-mono text-xs"
                      placeholder="vpn, network, email"
                      value={editForm.skills}
                      onChange={(e) => setEditForm({ ...editForm, skills: e.target.value })}
                    />
                  </div>
                )}
              </div>
              <DialogFooter className="sm:justify-between gap-2">
                {isSuper && editUser.id !== user?.id && !editUser.is_protected ? (
                  editUser.suspended ? (
                    <Button variant="outline" className="rounded-sm font-mono uppercase text-xs border-emerald-500/50 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-500/10 mr-auto" onClick={() => suspendUser(editUser, false)} disabled={suspendingId === editUser.id} data-testid="user-reactivate">
                      {suspendingId === editUser.id ? "…" : "Reactivate access"}
                    </Button>
                  ) : (
                    <Button variant="outline" className="rounded-sm font-mono uppercase text-xs border-red-500/50 text-red-700 dark:text-red-400 hover:bg-red-500/10 mr-auto" onClick={() => suspendUser(editUser, true)} disabled={suspendingId === editUser.id} data-testid="user-suspend">
                      {suspendingId === editUser.id ? "…" : "Suspend access"}
                    </Button>
                  )
                ) : <span />}
                <div className="flex gap-2">
                  <Button variant="outline" className="rounded-sm font-mono uppercase text-xs" onClick={() => setEditUser(null)} data-testid="user-edit-cancel">Cancel</Button>
                  <Button className="rounded-sm font-mono uppercase text-xs" onClick={saveEdit} disabled={savingEdit} data-testid="user-edit-save">
                    <FloppyDisk size={13} className="mr-1.5" /> {savingEdit ? "Saving…" : "Save changes"}
                  </Button>
                </div>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={auditOpen} onOpenChange={setAuditOpen}>
        <DialogContent className="rounded-sm max-w-2xl" data-testid="role-audit-dialog">
          <DialogHeader><DialogTitle className="font-mono uppercase text-sm flex items-center gap-2"><ClockCounterClockwise size={16} /> User change &amp; login history</DialogTitle></DialogHeader>
          <div className="flex flex-wrap items-center gap-2 pb-2 border-b border-border">
            <Select value={auditUser} onValueChange={setAuditUser}>
              <SelectTrigger className="h-8 w-48 rounded-sm text-xs" data-testid="audit-filter-user"><SelectValue placeholder="All users" /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>All users</SelectItem>
                {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={auditAction} onValueChange={setAuditAction}>
              <SelectTrigger className="h-8 w-40 rounded-sm text-xs" data-testid="audit-filter-action"><SelectValue placeholder="All actions" /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>All actions</SelectItem>
                {["role", "skills", "timesheet", "login", "logout", "failed_login"].map((a) => <SelectItem key={a} value={a}>{a.replace("_", " ")}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" className="h-8 rounded-sm font-mono uppercase text-xs ml-auto" onClick={exportAudit} data-testid="audit-export-csv"><PaperPlaneTilt size={13} className="mr-1.5 rotate-90" /> CSV</Button>
          </div>
          <div className="max-h-[55vh] overflow-y-auto -mx-1 px-1">
            {auditLoading ? (
              <div className="py-8 text-center text-sm text-muted-foreground">Loading…</div>
            ) : audit.length === 0 ? (
              <div className="py-8 text-center text-sm text-muted-foreground" data-testid="role-audit-empty">No history for this filter.</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground border-b border-border">
                  <tr><th className="text-left py-2">User</th><th className="text-left py-2">Event</th><th className="text-left py-2">By</th><th className="text-right py-2">When</th></tr>
                </thead>
                <tbody>
                  {audit.map((a) => {
                    const action = a.action || "role";
                    const oldV = a.old ?? a.old_role ?? "";
                    const newV = a.new ?? a.new_role ?? "";
                    const tag = { role: "border-primary/50 text-primary", skills: "border-sky-400/40 text-sky-700 dark:text-sky-300", timesheet: "border-teal-400/40 text-teal-600 dark:text-teal-300", login: "border-emerald-400/40 text-emerald-600 dark:text-emerald-400", logout: "border-zinc-400/40 text-muted-foreground", failed_login: "border-red-500/50 text-red-600 dark:text-red-400 bg-red-500/5" }[action] || "border-border text-muted-foreground";
                    const hasChange = oldV || newV;
                    return (
                      <tr key={a.id} className="border-b border-border/60 align-top" data-testid={`role-audit-row-${a.id}`}>
                        <td className="py-2 pr-2">
                          <div className="font-medium">{a.target_name}</div>
                          <div className="text-[10px] text-muted-foreground font-mono">{a.target_email}</div>
                        </td>
                        <td className="py-2 pr-2">
                          <span className={`inline-block text-[9px] font-mono uppercase px-1.5 py-0.5 border rounded-sm mr-2 ${tag}`}>{action}</span>
                          {hasChange && <span className="font-mono text-xs"><span className="text-muted-foreground">{String(oldV).replace(/_/g, " ")}</span><span className="mx-1">→</span><span className="text-primary font-semibold">{String(newV).replace(/_/g, " ")}</span></span>}
                          {a.detail && <div className="text-[10px] text-muted-foreground font-mono mt-0.5">{a.detail}</div>}
                        </td>
                        <td className="py-2 pr-2 text-xs">{a.by ?? a.changed_by_name}</td>
                        <td className="py-2 text-right text-[11px] font-mono text-muted-foreground whitespace-nowrap">{fmtIST(a.changed_at)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
